import React, { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import { SourceProjection } from '../../commands/practitioner/enums/SourceProjection';
import { Theme } from '../../../src/components/PhaserApp/enums/Themes';
import { ShowMessageCommandTypes } from '../../commands/practitioner/enums/ShowMessageCommandTypes';
import { TaskBallColor } from '../../commands/practitioner/enums/TaskBallColor';
import { useTranslation } from 'react-i18next';
import { UploadPanelTabs } from '../../commands/practitioner/enums/UploadPanelTabs';
import { ColorSwitchOptions } from '../../commands/practitioner/enums/ColorSwitchOptions';
import { StroopConfigurationOptions } from '../../commands/practitioner/enums/StroopConfigurationOptions';

export interface PractitionerControlsContext {
  eyeMovement: boolean;
  setEyeMovement: Dispatch<SetStateAction<boolean>>;
  trajectory: number;
  setTrajectory: Dispatch<SetStateAction<number>>;
  interactive: boolean;
  setInteractive: Dispatch<SetStateAction<boolean>>;
  autoSpeed: boolean;
  setAutoSpeed: Dispatch<SetStateAction<boolean>>;
  reactionVisual: boolean;
  setReactionVisual: Dispatch<SetStateAction<boolean>>;
  speed: number;
  setSpeed: Dispatch<SetStateAction<number>>;
  reactionAudio: boolean;
  setReactionAudio: Dispatch<SetStateAction<boolean>>;
  colorInterval: number;
  setColorInterval: Dispatch<SetStateAction<number>>;
  calculationDifficulty: number;
  setCalculationDifficulty: Dispatch<SetStateAction<number>>;
  sound: boolean;
  practitionerMediaType: MediaTypes;
  setPractitionerMediaType: Dispatch<SetStateAction<MediaTypes>>;
  setSound: Dispatch<SetStateAction<boolean>>;
  soundClicks: boolean;
  setSoundClicks: Dispatch<SetStateAction<boolean>>;
  soundPlops: boolean;
  setSoundPlops: Dispatch<SetStateAction<boolean>>;
  soundBuzzer: number;
  setSoundBuzzer: Dispatch<SetStateAction<number>>;
  sudScores: any[];
  setSudScores: Dispatch<SetStateAction<any[]>>;
  vocScores: any[];
  setVocScores: Dispatch<SetStateAction<any[]>>;
  notes: string;
  setNotes: Dispatch<SetStateAction<string>>;
  practitionerMediaURI: string;
  setPractitionerMediaURI: Dispatch<SetStateAction<string>>;
  isMediaLoaded: boolean;
  setIsMediaLoaded: Dispatch<SetStateAction<boolean>>;
  isConfirming: boolean;
  setIsConfirming: Dispatch<SetStateAction<boolean>>;
  isPractitionerPlayerVisible: boolean;
  setIsPractitionerPlayerVisible: Dispatch<SetStateAction<boolean>>;
  isPractitionerReactPlayerStarted: boolean;
  setIsPractitionerReactPlayerStarted: Dispatch<SetStateAction<boolean>>;
  hasBeenSaved: boolean;
  setHasBeenSaved: Dispatch<SetStateAction<boolean>>;
  taskHasStarted: boolean;
  setTaskHasStarted: Dispatch<SetStateAction<boolean>>;
  practitionerCommands: string[];
  setPractitionerCommands: Dispatch<SetStateAction<string[] | undefined>>;
  initialCommands: string[];
  projectionType: SourceProjection;
  setProjectionType: Dispatch<SetStateAction<SourceProjection>>;
  game: Phaser.Game | null;
  setGame: Dispatch<SetStateAction<Phaser.Game | null>>;
  isMutedYoutube: boolean;
  setIsMutedYoutube: Dispatch<SetStateAction<boolean>>;
  savedUri: string;
  setSavedUri: Dispatch<SetStateAction<string>>;
  lastTypeWhenURISaved: MediaTypes;
  setLastTypeWhenURISaved: Dispatch<SetStateAction<MediaTypes>>;
  reactionAnimation: boolean;
  setReactionAnimation: Dispatch<SetStateAction<boolean>>;
  stroopConfiguration: number;
  setStroopConfiguration: Dispatch<SetStateAction<number>>;
  theme: number;
  setTheme: Dispatch<SetStateAction<number>>;
  aidModal: number;
  setAidModal: Dispatch<SetStateAction<number>>;
  openMediaModal: boolean;
  setOpenMediaModal: Dispatch<SetStateAction<boolean>>;
  selectedColor: number;
  setSelectedColor: Dispatch<SetStateAction<number>>;
  triggerSpeedChange: boolean | undefined;
  setTriggerSpeedChange: Dispatch<SetStateAction<boolean | undefined>>;
  lastCalculationDifficulty: number | undefined;
  setLastCalculationDifficulty: Dispatch<SetStateAction<number | undefined>>;
  lastBuzzer: number | undefined;
  setLastBuzzer: Dispatch<SetStateAction<number | undefined>>;
  selectedOption: string;
  setSelectedOption: Dispatch<SetStateAction<string>>;
  status: boolean;
  setStatus: Dispatch<SetStateAction<boolean>>;
  startResetTimeout: boolean;
  setStartResetTimeout: Dispatch<SetStateAction<boolean>>;
  showVOCAidPressed: boolean;
  setShowVOCAidPressed: Dispatch<SetStateAction<boolean>>;
  showSUDAidPressed: boolean;
  setShowSUDAidPressed: Dispatch<SetStateAction<boolean>>;
  uploadUploaded: number;
  setUploadUploaded: Dispatch<SetStateAction<number>>;
  practitionerVideoTime: number;
  setPractitionerVideoTime: Dispatch<SetStateAction<number>>;
  viewSwapped: boolean;
  setViewSwapped: Dispatch<SetStateAction<boolean>>;
}

const VIEW_SWAP_STORAGE_KEY = 'practitioner-view-swap';

const readViewSwapInitial = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(VIEW_SWAP_STORAGE_KEY);
    if (raw === null) return false;
    return JSON.parse(raw) === true;
  } catch {
    return false;
  }
};

export const PractitionerControlsContext = createContext<PractitionerControlsContext>(null!);

interface PractitionerControlsProviderProps {
  children: ReactNode;
}

const PractitionerControlsProvider: React.FC<PractitionerControlsProviderProps> = ({ children }) => {
  const { t, i18n } = useTranslation();

  const [eyeMovement, setEyeMovement] = useState<boolean>(true);
  const [trajectory, setTrajectory] = useState<number>(2);
  const [interactive, setInteractive] = useState<boolean>(true);
  const [autoSpeed, setAutoSpeed] = useState<boolean>(false);
  const [reactionVisual, setReactionVisual] = useState<boolean>(true);
  const [speed, setSpeed] = useState<number>(1);
  const [reactionAudio, setReactionAudio] = useState<boolean>(false);
  const [colorInterval, setColorInterval] = useState<number>(ColorSwitchOptions.Automatically);
  const [calculationDifficulty, setCalculationDifficulty] = useState<number>(0);
  const [lastCalculationDifficulty, setLastCalculationDifficulty] = useState<number | undefined>(undefined);
  const [sound, setSound] = useState<boolean>(true);
  const [soundClicks, setSoundClicks] = useState<boolean>(true);
  const [soundPlops, setSoundPlops] = useState<boolean>(false);
  const [soundBuzzer, setSoundBuzzer] = useState<number>(0);
  const [lastBuzzer, setLastBuzzer] = useState<number | undefined>(undefined);
  const [taskHasStarted, setTaskHasStarted] = useState<boolean>(false);
  const [reactionAnimation, setReactionAnimation] = useState<boolean>(false);
  const [stroopConfiguration, setStroopConfiguration] = useState<number>(StroopConfigurationOptions.None);
  const [theme, setTheme] = useState<number>(Theme.Default);
  const [selectedColor, setSelectedColor] = useState(TaskBallColor.Silver);

  // session log info
  const [sudScores, setSudScores] = useState<any[]>([]);
  const [vocScores, setVocScores] = useState<any[]>([]);
  const [notes, setNotes] = useState<string>('');

  // media online tab
  const [practitionerMediaURI, setPractitionerMediaURI] = useState<string>('');
  const [isMediaLoaded, setIsMediaLoaded] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isPractitionerPlayerVisible, setIsPractitionerPlayerVisible] = useState<boolean>(false);
  const [isPractitionerReactPlayerStarted, setIsPractitionerReactPlayerStarted] = useState<boolean>(false);
  const [hasBeenSaved, setHasBeenSaved] = useState<boolean>(false);
  const [practitionerMediaType, setPractitionerMediaType] = useState<MediaTypes>(MediaTypes.VIDEO);
  const [projectionType, setProjectionType] = useState<SourceProjection>(SourceProjection.Flat);
  const [isMutedYoutube, setIsMutedYoutube] = useState<boolean>(true);
  const [savedUri, setSavedUri] = useState<string>('');
  const [lastTypeWhenURISaved, setLastTypeWhenURISaved] = useState<MediaTypes>(MediaTypes.VIDEO);
  const [aidModal, setAidModal] = useState(ShowMessageCommandTypes.Hide);
  const [openMediaModal, setOpenMediaModal] = useState<boolean>(false);

  // practitioner command that are being polled
  const [practitionerCommands, setPractitionerCommands] = useState<string[] | undefined>(undefined);
  const [initialCommands, setInitialCommands] = useState<string[]>([]);
  const [hasSetInitialCommands, setHasSetInitialCommands] = useState<boolean>(false);
  const [triggerSpeedChange, setTriggerSpeedChange] = useState<boolean | undefined>(undefined);
  const [practitionerVideoTime, setPractitionerVideoTime] = useState<number>(0);

  const [selectedOption, setSelectedOption] = useState(i18n.language);
  const [startResetTimeout, setStartResetTimeout] = useState<boolean>(false);
  const [showVOCAidPressed, setShowVOCAidPressed] = useState(false);
  const [showSUDAidPressed, setShowSUDAidPressed] = useState(false);

  //media upload tab
  const [status, setStatus] = useState<boolean>(false);
  const [uploadUploaded, setUploadUploaded] = useState(UploadPanelTabs.Upload);

  // view swap (main vs client-view placement of camera/media)
  const [viewSwapped, setViewSwappedState] = useState<boolean>(() => readViewSwapInitial());
  const setViewSwapped: Dispatch<SetStateAction<boolean>> = (value) => {
    setViewSwappedState((prev) => {
      const next = typeof value === 'function' ? (value as (p: boolean) => boolean)(prev) : value;
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(VIEW_SWAP_STORAGE_KEY, JSON.stringify(next));
        }
      } catch {
        // localStorage unavailable — keep in-memory state only
      }
      return next;
    });
  };

  // phaser game init
  const [game, setGame] = useState<null | Phaser.Game>(null);

  useEffect(() => {
    if (!hasSetInitialCommands && practitionerCommands) {
      setHasSetInitialCommands(true);
      setInitialCommands(practitionerCommands);
    }
  }, [practitionerCommands, hasSetInitialCommands]);

  // Memoized the value of the commands that should be interpreted for media
  const actualPractitionerCommands: string[] = useMemo(() => {
    if (!practitionerCommands || !hasSetInitialCommands) {
      return [];
    }
    const initialCommandsLength = initialCommands.length;
    const practitionerCommandsLength = practitionerCommands.length;
    return practitionerCommands.splice(initialCommandsLength, practitionerCommandsLength);
  }, [practitionerCommands, initialCommands, hasSetInitialCommands]);

  return (
    <PractitionerControlsContext.Provider
      value={{
        openMediaModal,
        setOpenMediaModal,
        selectedColor,
        setSelectedColor,
        notes,
        sudScores,
        vocScores,
        eyeMovement,
        trajectory,
        interactive,
        autoSpeed,
        reactionVisual,
        speed,
        colorInterval,
        calculationDifficulty,
        sound,
        practitionerMediaURI,
        soundClicks,
        soundPlops,
        soundBuzzer,
        reactionAudio,
        practitionerMediaType,
        setPractitionerMediaType,
        setEyeMovement,
        setTrajectory,
        setInteractive,
        setAutoSpeed,
        setReactionVisual,
        setSpeed,
        setReactionAudio,
        setColorInterval,
        setCalculationDifficulty,
        setSound,
        setSoundClicks,
        setSoundPlops,
        setSoundBuzzer,
        setNotes,
        setSudScores,
        setVocScores,
        setPractitionerMediaURI,
        isMediaLoaded,
        setIsMediaLoaded,
        isConfirming,
        setIsConfirming,
        isPractitionerPlayerVisible,
        setIsPractitionerPlayerVisible,
        isPractitionerReactPlayerStarted,
        setIsPractitionerReactPlayerStarted,
        hasBeenSaved,
        setHasBeenSaved,
        setPractitionerCommands,
        initialCommands,
        practitionerCommands: actualPractitionerCommands,
        taskHasStarted,
        setTaskHasStarted,
        projectionType,
        setProjectionType,
        game,
        setGame,
        setIsMutedYoutube,
        isMutedYoutube,
        savedUri,
        setSavedUri,
        lastTypeWhenURISaved,
        setLastTypeWhenURISaved,
        setReactionAnimation,
        reactionAnimation,
        stroopConfiguration,
        setStroopConfiguration,
        theme,
        setTheme,
        aidModal,
        setAidModal,
        triggerSpeedChange,
        setTriggerSpeedChange,
        lastCalculationDifficulty,
        setLastCalculationDifficulty,
        lastBuzzer,
        setLastBuzzer,
        selectedOption,
        setSelectedOption,
        status,
        setStatus,
        startResetTimeout,
        setStartResetTimeout,
        showVOCAidPressed,
        setShowVOCAidPressed,
        showSUDAidPressed,
        setShowSUDAidPressed,
        uploadUploaded,
        setUploadUploaded,
        practitionerVideoTime,
        setPractitionerVideoTime,
        viewSwapped,
        setViewSwapped,
      }}
    >
      {children}
    </PractitionerControlsContext.Provider>
  );
};

export default PractitionerControlsProvider;
